import { ReactNode, useMemo } from 'react'
import { core } from '@musinsa/pbo-core'
import type { MenuButtonPermission, ToolbarConfig } from '@/services/auth/menu.types'
import { useGetMenuList } from '@/services/auth/useAuthQueries'

// LOGISPRD-16785 — 버튼 권한은 BE 가 레벨·화면관리 BUTTONS 로 계산해 /me/menus 메뉴 항목에 싣는다(LOGISPRD-16782). FE 는 읽기만 한다.
// 값이 null·없음이면 그 항목은 제한 없음 — flag OFF·authMenu OFF(1.0 메뉴 API)·메뉴 없음. 세 값은 따로 null 일 수 있다.
// 메뉴 조회 전·실패 땐 MenuGuard 가 화면을 그리지 않아 "로드 전" 판정은 화면에 닿지 않는다.
export interface MenuPermission {
  /** code 는 화면관리 BUTTONS 와 같은 버튼 코드. 없으면(태그 없는 버튼) 판정 대상이 아니라 항상 true. */
  canShow: (code?: string) => boolean
  /** 화면 설정값을 응답 toolbar 로 거른다 — 화면에서 꺼진 것을 켜지 않는다. */
  toolbar: (config: ToolbarConfig) => ToolbarConfig
  /** 셀 편집 */
  editable: boolean
}

export const resolveMenuPermission = ({ allowedButtons, toolbar, editable }: MenuButtonPermission = {}): MenuPermission => ({
  canShow: (code) => !code || !allowedButtons || allowedButtons.includes(code),
  toolbar: (config) =>
    toolbar
      ? {
          add: config.add && toolbar.add,
          remove: config.remove && toolbar.remove,
          upload: config.upload && toolbar.upload,
          download: config.download && toolbar.download,
        }
      : config,
  editable: editable ?? true,
})

/** menuCode 는 라우트 경로와 같은 소문자(useGetMenuList select 가 바꾼 값). */
export const useMenuPermission = (menuCode: string): MenuPermission => {
  const { data } = useGetMenuList()
  // select 결과는 구조 공유라 내용이 같으면 같은 참조다.
  const item = data?.flatMap((parent) => parent.menuItem).find((child) => child.menuCode === menuCode)

  return useMemo(() => resolveMenuPermission(item), [item])
}

/**
 * 셀 편집 잠금 — 편집 셀을 글자로 보여 준다(LOGISPRD-16733). pbo-core Table 은 Delete·채우기를 variant input·amountInput·editable 열,
 * 타이핑을 input 열에만 받으므로 variant 와 editable 을 함께 바꾸면 그 경로도 막힌다.
 * 붙여넣기는 pbo-core 0.8.6 이 열 종류를 보지 않아 잠긴 열에도 들어간다 — DS 에 수정을 요청했다(붙여넣기 대상을 편집 셀로 좁히면 막힌다).
 * attach·video 등 열람 셀은 그대로 둔다.
 */
export const lockColumns = (columns: core.CoreColumnType[]): core.CoreColumnType[] =>
  columns.map((column) => {
    const col = { ...column, editable: undefined }
    switch (col.variant) {
      case 'select': {
        const options = col.variantProps?.options ?? []
        return { ...col, variant: undefined, render: (value: ReactNode) => options.find((option) => option.value === value)?.label ?? value }
      }
      case 'amountInput':
        return { ...col, variant: 'comma' }
      case 'input':
      case 'datepicker':
      case 'checkbox':
      case 'switch':
      case 'helper':
        return { ...col, variant: undefined }
      default:
        return col
    }
  })

const ALL_TOOLS: ToolbarConfig = { add: true, remove: true, upload: true, download: true }
const unlocked = (columns: core.CoreColumnType[]) => columns

/** 생성본 Wiring.permission 슬롯 값(LOGISPRD-16733). 생성본 툴바 버튼은 화면에 있는 것이라 화면 설정을 모두 켠 값으로 거른다. */
export const usePermissionSlot = (menuCode: string) => {
  const permission = useMenuPermission(menuCode)

  return useMemo(
    () => ({
      canShow: permission.canShow,
      toolbar: permission.toolbar(ALL_TOOLS),
      editable: permission.editable,
      columns: permission.editable ? unlocked : lockColumns,
    }),
    [permission],
  )
}
